import { useEffect, useState } from 'react';
import MediaGallery from '../../../../components/MediaGallery/MediaGallery';
import './NewDefault.sass';
import { getPriceWithCurrency, getSavingsPercent } from '../../../../utils/helpers';

export default function NewDefault({ specialOffer, onSubmit, offer }) {
  const TIMER_DURATION_SECONDS = 120;
  const safeProductsData = Array.isArray(specialOffer.productsData) ? specialOffer.productsData : [];
  const mergedProductsData = safeProductsData.map((product) => {
    const productDetail = specialOffer.productDetails?.[product.id] || {};
    return { ...product, ...productDetail };
  });

  const [selectedProduct] = useState(mergedProductsData[0]);
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION_SECONDS);

  useEffect(() => {
    setTimeLeft(TIMER_DURATION_SECONDS);
  }, [specialOffer._id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const activeProduct = selectedProduct || mergedProductsData[0];
  const oldPrice = activeProduct?.oldPrice ? Number(activeProduct.oldPrice) : 0;
  const price = activeProduct?.price ? Number(activeProduct.price) : 0;
  const hasDiscount = oldPrice > 0 && price > 0 && oldPrice > price;
  const percentOff = hasDiscount ? getSavingsPercent(oldPrice, price) : 0;
  const savingsAmount = hasDiscount ? oldPrice - price : 0;
  const msrpDisplay = getPriceWithCurrency(oldPrice, offer?.locale, offer?.currency);
  const priceDisplay = getPriceWithCurrency(activeProduct?.price, offer?.locale, offer?.currency) || '$0.00';
  const savingsDisplay = hasDiscount
    ? getPriceWithCurrency(savingsAmount, offer?.locale, offer?.currency)
    : '';
  const formattedTime = `${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(
    timeLeft % 60
  ).padStart(2, '0')}`;
  const progressPercent = Math.max(0, Math.min(1, timeLeft / TIMER_DURATION_SECONDS)) * 100;

  function handleSubmit() {
    if (activeProduct) {
      onSubmit(activeProduct);
    } else {
      onSubmit();
    }
  }

  function renderStars(rating) {
    const stars = [];
    const safeRating = Number.isFinite(rating) ? rating : 5;
    const fullStars = Math.floor(safeRating);
    const hasHalfStar = safeRating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={`full-${i}`} className='fas fa-star'></i>);
    }
    if (hasHalfStar) {
      stars.push(<i key='half' className='fas fa-star-half-alt'></i>);
    }
    const emptyStars = 5 - Math.ceil(safeRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className='far fa-star'></i>);
    }
    return stars;
  }

  const benefits = Array.isArray(specialOffer.benefits)
    ? specialOffer.benefits.filter((b) => Boolean(b))
    : [];

  function renderBenefits() {
    return (
      <>
        {benefits.length > 0 && (
          <div className='nd-benefits-section'>
            <h4 className='nd-benefits-title'>Key Benefits</h4>
            <ul className='nd-benefits'>
              {benefits.map((benefit, index) => (
                <li key={`${benefit}-${index}`}>
                  <span className='nd-benefits__icon' aria-hidden='true'>
                    <span className='nd-benefits__icon-check'>✓</span>
                  </span>
                  <span className='nd-benefits__text'>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(specialOffer.descriptionTitle || specialOffer.description) && (
          <div className='nd-description'>
            {specialOffer.descriptionTitle && (
              <h4 className='nd-description__title'>{specialOffer.descriptionTitle}</h4>
            )}
            {specialOffer.description && <p className='nd-description__text'>{specialOffer.description}</p>}
          </div>
        )}
      </>
    );
  }

  return (
    <div className='NewDefault'>
      <header className='nd-header'>
        <div className='nd-container'>
          <div className='nd-header__badge'>🎉 Your Order Is Confirmed! Before We Ship It...</div>
        </div>
      </header>

      <main className='nd-main'>
        <div className='nd-container'>
          {specialOffer.headline && <h1 className='nd-headline'>{specialOffer.headline}</h1>}
          {specialOffer.subHeadline && <h2 className='nd-subheadline'>{specialOffer.subHeadline}</h2>}

          <div className='nd-layout'>
            <div className='nd-left'>
              <MediaGallery items={specialOffer.gallery} variant='square' />
            </div>

            <div className='nd-right'>
              {specialOffer.productName && <h3 className='nd-product-name'>{specialOffer.productName}</h3>}

              {(specialOffer.delivered || benefits.length > 0) && (
                <div className='nd-meta'>
                  <div className='nd-meta__stars'>{renderStars(5)}</div>
                  {specialOffer.delivered && (
                    <span className='nd-meta__delivered'>{specialOffer.delivered} Delivered</span>
                  )}
                </div>
              )}

              <div className='nd-desk-only'>{renderBenefits()}</div>

              <div className='nd-pricing'>
                {msrpDisplay && hasDiscount && (
                  <div className='nd-pricing__regular'>
                    <div className='nd-pricing__value'>
                      <span className='nd-pricing__label'>Regular Price:</span>
                      <span className='nd-pricing__msrp'>{msrpDisplay}</span>
                    </div>

                    <span className='nd-pricing__badge'>{percentOff}% OFF</span>
                  </div>
                )}

                <div className='nd-pricing__special'>
                  <div>
                    <span className='nd-pricing__label'>Your Exclusive Price</span>
                    <span className='nd-pricing__price'>{priceDisplay}</span>
                  </div>
                  <div className='nd-pricing__right'>
                    <span className='nd-pricing__shipping'>+ FREE SHIPPING</span>
                    {hasDiscount && savingsDisplay && (
                      <span className='nd-pricing__savings'>You save {savingsDisplay}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className='nd-timer'>
                <div className='nd-timer__text'>
                  <i className='fas fa-stopwatch'></i>
                  <span>Reserved for you for the next {formattedTime}</span>
                </div>
                <div className='nd-timer__bar'>
                  <div className='nd-timer__fill' style={{ width: `${progressPercent}%` }}></div>
                </div>
              </div>

              <button className='nd-cta' onClick={handleSubmit}>
                <span className='nd-cta__main'>
                  {specialOffer.ctaText || 'Yes! Add My 75% OFF Sleeve Now'}
                </span>
                <span className='nd-cta__sub'>Ships with my order</span>
              </button>

              <p className='nd-notice'>This one-time offer is only available on this page.</p>

              <div className='nd-security'>
                {specialOffer.shippingText ? (
                  <div
                    className='nd-security__text'
                    dangerouslySetInnerHTML={{ __html: specialOffer.shippingText }}
                  />
                ) : (
                  <p className='nd-security__text'>
                    Your payment info is already secure — this add-on will be processed instantly with your
                    existing order.
                  </p>
                )}
              </div>

              <div className='nd-badges'>
                <i className='fab fa-cc-visa'></i>
                <i className='fab fa-cc-mastercard'></i>
                <i className='fab fa-cc-amex'></i>
                <i className='fab fa-cc-paypal'></i>
              </div>

              <button type='button' className='nd-decline' onClick={() => onSubmit()}>
                No thanks, I'll pass on this one
              </button>
            </div>

            <div className='nd-mob-only'>{renderBenefits()}</div>
          </div>

          {/* Testimonials Section */}
          {Array.isArray(specialOffer.testimonials) && specialOffer.testimonials.length > 0 && (
            <div className='nd-testimonials'>
              <h2 className='nd-testimonials__title'>What Our Customers Say</h2>
              <div className='nd-testimonials__grid'>
                {specialOffer.testimonials.slice(0, 3).map((testimonial, index) => (
                  <div key={`testimonial-${index}`} className='nd-testimonial'>
                    <div className='nd-testimonial__header'>
                      {testimonial.image && (
                        <img
                          src={testimonial.image}
                          alt={testimonial.author || 'Customer'}
                          className='nd-testimonial__avatar'
                        />
                      )}
                      <div className='nd-testimonial__meta'>
                        {testimonial.author && (
                          <div className='nd-testimonial__author'>{testimonial.author}</div>
                        )}
                        {testimonial.stars && (
                          <div className='nd-testimonial__stars'>{renderStars(testimonial.stars)}</div>
                        )}
                      </div>
                    </div>
                    {testimonial.text && <p className='nd-testimonial__text'>{testimonial.text}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='nd-mob-only'>
            <button className='nd-cta' onClick={handleSubmit}>
              <span className='nd-cta__main'>{specialOffer.ctaText || 'Yes! Add My 75% OFF Sleeve Now'}</span>
              <span className='nd-cta__sub'>Ships with my order</span>
            </button>
            <p className='nd-notice'>This one-time offer is only available on this page.</p>
          </div>

          {/* Shipping Info */}
          <div className='nd-shipping-info nd-desk-only'>
            <p className='nd-shipping-info__text'>
              All orders ship from the USA via USPS within 1 business day. 30-day money-back guarantee on all
              products.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
